import { Component, OnInit } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { ColDef, ColumnState, DateFilterModel, SetFilterModel, TextFilterModel } from 'ag-grid-community';
import { filter, map, Observable, Subject, takeUntil, withLatestFrom } from 'rxjs';
import { selectMasterAccount } from '../../../account-info-header/reducers';
import { ActionsLinkComponent } from '../../../shared/actions-link/actions-link.component';
import { AccountCellRendererComponent } from '../../../shared/components/account-cell-renderer/account-cell-renderer.component';
import { NamePipe } from '../../../shared/pipes/NamePipe';
import { agFilterNames } from '../../../shared/types/AgFilterNames';
import { MasterTransactionsTableColumn } from '../../../shared/types/MasterTransactionsTableColumn';
import { TableComponentState } from '../../../shared/types/TableComponentState';
import { activityReportDisclaimerText, Constants, masterTransactionsTableTabs, transactionTypes } from '../../../shared/utilities/Constants';
import { PreferenceTab } from '../../../user-preference/models/PreferenceTab';
import { selectMasterActivityReportTab, selectMasterRecurringTab, selectMasterTransactionStatusTab, selectPreferencesLoaded } from '../../../user-preference/reducers';
import { ActivityReportTableActions, MasterStatusViewActions, MasterTransactionsTableActions, PtrsActions } from '../../actions';
import { gridReady } from '../../actions/master-transactions-table.actions';
import { MsvTransaction, MsvTransactionAttributes } from '../../models/master-status-view/TransactionsResponse';
import { selectActivityReports, selectMasterTransactionsTableState, selectMasterTransactionStatusData } from '../../reducers';
import { amountColumnTemplate, currencyComparator, dateComparator, dateComparatorForFilter, defaultColumnTemplate, sharedColNames } from '../transactions-table/table-helpers';
import { defaultMasterActivityReportTabColumnDefs, defaultMasterRecurringTabColumnDefs, defaultMasterTransactionsTabColumnDefs } from './master-transaction-table-definitions';
import { MasterActivityReportTableColumn } from '../../../shared/types/MasterActivityReportTableColumn';
import { TableColumn } from '../../../shared/types/TableColumn';
import { ActivityReportDownloadFieldComponent } from '../../../shared/components/activity-report-download-field/activity-report-download-field.component';
import { ActivityReport } from '../../models/master-status-view/ActivityReportState';

export const masterTransactionTableStateKey = 'masterTransactionsTableState';

@Component({
  standalone: false,
  selector: 'mm-master-transaction-table',
  templateUrl: './master-transactions-table.component.html',
  styleUrls: ['./master-transactions-table.component.scss']
})
export class MasterTransactionsTableComponent implements OnInit {
  availableCashToolTipText: string = Constants.availableCash;
  atpReturnLimitMessage: string = Constants.atpReturnLimitMessage;
  activityReportDisclaimerText: string[] = activityReportDisclaimerText;
  colNames = {
    actions: sharedColNames.actions,
    accountNumber: 'Account number',
    accountName: 'Account name',
    registrationType: 'Registration type',
    processDate: 'Process date',
    nextProcessDate: 'Next process date',
    dateRange: 'Date',
    transactionType: sharedColNames.transactionType,
    toFromAccount: sharedColNames.toFromAccount,
    direction: 'Direction',
    amount: 'Amount',
    availableCash: 'Available cash',
    status: sharedColNames.status,
    frequency: 'Frequency',
    activityReportDownload: 'ActivityReportDownload'
  }

  actionsColumnDef: ColDef[] = [{
    field: this.colNames.actions,
    sortable: false,
    cellRenderer: ActionsLinkComponent,
    cellRendererParams: {
      onClick: this.actionLinkClicked.bind(this),
      viewStatus: 'View'
    },
    minWidth: 120,
    maxWidth: 120,
    flex: 1,
  }];

  activityReportDownload: ColDef[] = [{
    headerName: '',
    field: this.colNames.activityReportDownload,
    sortable: false,
    filter: false,
    minWidth: 100,
    maxWidth: 100,
    floatingFilter: false,
    cellRenderer: ActivityReportDownloadFieldComponent,
    cellRendererParams: {
      onClick: this.downloadClicked.bind(this),
    }
  }]

  filterModel: {
    [key: string]: TextFilterModel | DateFilterModel | SetFilterModel;
  };

  masterTransactionsTableState$: Observable<TableComponentState>;
  masterTransactionsColumnDefs: ColDef[] = [];
  currentSelectedTabDefaults: ColDef[] = [];
  rowData$: Observable<TableColumn[]>
  currentRowTransaction: MasterTransactionsTableColumn;
  currentTab: string;
  destroy$ = new Subject<void>();
  currentPrefsTab: PreferenceTab;
  prefsLoaded: boolean = false;
  hasNoTransactions: boolean = false;
  hasNoRecurringTransactions: boolean = false;
  hasNoActivityReport: boolean = false;
  fileName: string;
  currentActionLinkClickedEvent: any;
  columnState: ColumnState[] | undefined;
  columnTab: string;
  isActivityReportTab: boolean = false;
  accountNumber: string;
  isTooManyRecords: boolean = false;
  currentTabSelector: any = selectMasterTransactionStatusData;
  noExportCol: string[] = ['Actions'];


  constructor(
    private store: Store,
    private namePipe: NamePipe,
  ) { }

  ngOnInit(): void {
    this.currentSelectedTabDefaults = this.actionsColumnDef.concat(defaultMasterTransactionsTabColumnDefs);
    this.masterTransactionsColumnDefs = this.actionsColumnDef.concat(defaultMasterTransactionsTabColumnDefs);
    this.currentTab = 'Transaction Status';

    this.store.select(selectPreferencesLoaded)
      .pipe(
        takeUntil(this.destroy$),
        withLatestFrom(
          this.store.select(selectMasterTransactionStatusTab)
        ),
        filter(([loaded]) => loaded === true)
      ).subscribe(([_loaded, tab]) => {
        if (tab && tab.columns?.length !== 0) {
          this.masterTransactionsColumnDefs = this.buildColumnPreferences(tab.columns, tab.tabName);
        }
      });
    this.masterTransactionsTableState$ = this.store.select(selectMasterTransactionsTableState);

    // Wait for the masterAccount to be available before dispatching getMasterTransactionsList
    this.store.select(selectMasterAccount)
    .pipe(
      takeUntil(this.destroy$),
      filter((accountNumber) => !!accountNumber && accountNumber !== this.accountNumber) // Ensure masterAccount is not null or undefined
    )
    .subscribe((accountNumber) => {
      this.accountNumber = accountNumber.padStart(8, '0');
      this.fileName = `MoveMoneyMasterTransactionStatusTxnList_${this.accountNumber}_`;

      // Dispatch getMasterTransactionsList after masterAccount is available
      this.store.dispatch(MasterStatusViewActions.getMasterTransactionsList());
    });

    this.setActiveTableEventListener();

    this.store.select(selectPreferencesLoaded)
      .pipe(
        takeUntil(this.destroy$),
        withLatestFrom(
          this.store.select(selectMasterTransactionStatusTab)
        )
      ).subscribe(([loaded, tab]) => {
        this.prefsLoaded = loaded;
        if (tab) {
          this.currentPrefsTab = tab;
        }
      });

    this.store.select(selectMasterTransactionStatusData).pipe(takeUntil(this.destroy$)).subscribe((state) => {
      // this field is set the same way for all transactions returned by transactions api so we can get the field from any of the transacions
      this.isTooManyRecords = state?.at(0)?.attributes?.isTooManyRecords ?? false;
    });
    this.getTableData();
  }

  getTableData(){
    if (this.isActivityReportTab) {
      this.rowData$ = this.store.select(selectActivityReports).pipe(
        map((activityReports) => {
          return this.mapMasterActivityReportsDataToRowData(activityReports)
        }),
        takeUntil(this.destroy$)
      );
    } else {
      this.rowData$ = this.store.select(selectMasterTransactionStatusData).pipe(
        map((cashieringTransactions) => {
          return this.mapTransactionListDataToRowData(cashieringTransactions)
        }),
        takeUntil(this.destroy$)
      );
    }
  }

  buildColumnPreferences(preferences: ColumnState[], tabName: string): ColDef[] {
    let updatedPreferences: ColumnState[] = [];
    preferences.forEach(preference => {
      if (isNaN(Number(preference.colId))) {
        updatedPreferences.push(preference);
      }
    });

    let result: ColDef[] = updatedPreferences.map((preference) => {
      switch (preference.colId) {

        case 'Actions':
          return {
            field: this.colNames.actions,
            sortable: false,
            filter: agFilterNames.set,
            floatingFilter: false,
            cellRenderer: ActionsLinkComponent,
            cellRendererParams: {
              onClick: this.actionLinkClicked.bind(this),
              viewStatus: 'View'
            },
            minWidth: 120,
            maxWidth: 120,
            flex: 1,
          }

        case 'Account number':
          return {
            field: this.colNames.accountNumber,
            sortIndex: tabName === 'transaction status' ? 0 : tabName === 'recurring' ? 1 : null,
            sort: preference.sort,
            floatingFilterComponent: 'customTextFloatingFilter',
            floatingFilterComponentParams: {
              suppressFilterButton: true,
            },
            headerComponentParams: { template: defaultColumnTemplate },
            flex: 1,
            minWidth: 140,
            cellRenderer: AccountCellRendererComponent,
            headerClass: params => params.column?.getSort() ? 'sorted-header-bold' : ''
          }

        case 'Account name':
          return {
            field: this.colNames.accountName,
            sort: preference.sort,
            floatingFilterComponent: 'customTextFloatingFilter',
            floatingFilterComponentParams: {
              suppressFilterButton: true,
            },
            headerComponentParams: { template: defaultColumnTemplate },
            minWidth: 140,
            headerClass: params => params.column?.getSort() ? 'sorted-header-bold' : ''
          }

        case 'Registration type':
          return {
            field: this.colNames.registrationType,
            filter: agFilterNames.set,
            sort: preference.sort,
            floatingFilterComponent: 'customSetFloatingFilter',
            floatingFilterComponentParams: {
              suppressFilterButton: true,
            },
            headerComponentParams: { template: defaultColumnTemplate },
            flex: 1,
            minWidth: 120,
            headerClass: params => params.column?.getSort() ? 'sorted-header-bold' : ''
          }

        case 'Process date':
          return {
            field: this.colNames.processDate,
            comparator: dateComparator,
            filter: agFilterNames.date,
            sort: preference.sort,
            filterParams: {
              comparator: dateComparatorForFilter,
              inRangeInclusive: true,
              browserDatePicker: true
            },
            headerComponentParams: { template: defaultColumnTemplate },
            minWidth: 160,
            maxWidth: 160,
            headerClass: params => params.column?.getSort() ? 'sorted-header-bold' : ''
          }
        case '':
          return {
            field: '',
            sortable: false,
            filter: false,
            minWidth: 160,
            maxWidth: 160
          }
        case 'ActivityReportDownload':
          return {
            headerName: '',
            field: this.colNames.activityReportDownload,
            sortable: false,
            filter: false,
            minWidth: 100,
            maxWidth: 100,
            floatingFilter: false,
            cellRenderer: ActivityReportDownloadFieldComponent,
            cellRendererParams: {
              onClick: this.downloadClicked.bind(this),
            }
          }

        case 'Date':
          return {
            field: this.colNames.dateRange,
            filter: false,
            sortable: false,
            minWidth: 260,
            maxWidth: 260,
            floatingFilter: false,
          }

        case 'Next process date':
          return {
            field: this.colNames.nextProcessDate,
            comparator: dateComparator,
            filter: agFilterNames.date,
            sortIndex: tabName === 'recurring' ? 0 : null,
            sort: preference.sort,
            filterParams: {
              comparator: dateComparatorForFilter,
              inRangeInclusive: true,
              browserDatePicker: true
            },
            headerComponentParams: { template: defaultColumnTemplate },
            minWidth: 160,
            maxWidth: 160,
            headerClass: params => params.column?.getSort() ? 'sorted-header-bold' : ''
          }

        case 'Type':
          return {
            field: this.colNames.transactionType,
            filter: agFilterNames.set,
            sort: preference.sort,
            floatingFilterComponent: 'customSetFloatingFilter',
            floatingFilterComponentParams: {
              suppressFilterButton: true,
            },
            headerComponentParams: { template: defaultColumnTemplate },
            flex: 1,
            minWidth: 120,
            headerClass: params => params.column?.getSort() ? 'sorted-header-bold' : ''
          }

        case 'To/From':
          return {
            field: this.colNames.toFromAccount,
            sort: preference.sort,
            floatingFilterComponent: 'customTextFloatingFilter',
            floatingFilterComponentParams: {
              suppressFilterButton: true,
            },
            headerComponentParams: { template: defaultColumnTemplate },
            filterParams: {
              textFormatter: (value: string) => value == null ? null : value.toLowerCase().replaceAll('-', '')
            },
            flex: 1,
            minWidth: 120,
            headerClass: params => params.column?.getSort() ? 'sorted-header-bold' : ''
          }

        case 'Direction':
          return {
            field: this.colNames.direction,
            sort: preference.sort,
            flex: 1,
            floatingFilterComponent: 'customSetFloatingFilter',
            floatingFilterComponentParams: {
              suppressFilterButton: true,
            },
            headerComponentParams: { template: defaultColumnTemplate },
            filter: agFilterNames.set,
            minWidth: 120,
            hide: false,
            pinned: null,
            pivot: false,
            pivotIndex: null,
            rowGroup: false,
            rowGroupIndex: null,
            sortIndex: null,
            aggFunc: null,
            headerClass: params => params.column?.getSort() ? 'sorted-header-bold' : ''
          }

        case 'Amount':
          return {
            field: this.colNames.amount,
            comparator: currencyComparator,
            sort: preference.sort,
            floatingFilterComponent: 'customTextFloatingFilter',
            floatingFilterComponentParams: {
              suppressFilterButton: true,
            },
            filterParams: {
              textFormatter: (value: string) => value == null ? null : value.toLowerCase().replaceAll(',', '')
            },
            type: 'rightAligned',
            headerComponentParams: { template: amountColumnTemplate },
            minWidth: 120,
            headerClass: params => params.column?.getSort() ? 'sorted-header-bold' : ''
          }

        case 'Available cash':
          return {
            headerTooltip: this.availableCashToolTipText,
            field: this.colNames.availableCash,
            comparator: currencyComparator,
            sort: preference.sort,
            floatingFilterComponent: 'customTextFloatingFilter',
            floatingFilterComponentParams: {
              suppressFilterButton: true,
            },
            filterParams: {
              textFormatter: (value: string) => value == null ? null : value.toLowerCase().replaceAll(',', '')
            },
            type: 'rightAligned',
            headerComponentParams: { template: amountColumnTemplate },
            minWidth: 120,
            headerClass: params => params.column?.getSort() ? 'sorted-header-bold' : ''
          }

        case 'Frequency':
          return {
            field: 'Frequency',
            filter: agFilterNames.set,
            sort: preference.sort,
            floatingFilterComponent: 'customSetFloatingFilter',
            floatingFilterComponentParams: {
              suppressFilterButton: true,
            },
            headerComponentParams: { template: defaultColumnTemplate },
            width: 120,
            minWidth: 120,
            maxWidth: 130,
            headerClass: params => params.column?.getSort() ? 'sorted-header-bold' : ''
          }

        case 'Status':
          return {
            field: this.colNames.status,
            filter: agFilterNames.set,
            sort: preference.sort,
            floatingFilterComponent: 'customSetFloatingFilter',
            floatingFilterComponentParams: {
              suppressFilterButton: true,
            },
            headerComponentParams: { template: defaultColumnTemplate },
            minWidth: 120,
            headerClass: params => params.column?.getSort() ? 'sorted-header-bold' : ''
          }

        default:
          return {}
      }
    });
    return result;
  }

  private setActiveTableEventListener(): void {
    const activeTable = document.getElementById('master-transaction-table-tabs');
    activeTable?.addEventListener(
      'sdpsSelect',
      ((event: Event) => {
        // sdpsSelect is a custom Event from Everest
        // e.detail contains Id of sdpsSelct
        const e = event as CustomEvent;
        this.isActivityReportTab = false;
        this.isTooManyRecords = false;
        switch (e.detail as string) {
          case 'transactionStatus':
            this.currentSelectedTabDefaults = this.actionsColumnDef.concat(defaultMasterTransactionsTabColumnDefs);
            this.fileName = `MoveMoneyMasterTransactionStatusTxnList_${this.accountNumber}_`
            this.store.select(selectMasterTransactionStatusTab).pipe(takeUntil(this.destroy$)).subscribe(tab => {
              this.columnState = tab?.columns;
              this.columnTab = tab?.tabName ?? '';
            });
            this.currentTabSelector = selectMasterTransactionStatusData;
            if (this.columnState && this.columnTab) {
              this.masterTransactionsColumnDefs = this.buildColumnPreferences(this.columnState, this.columnTab);
            }
            else {
              this.masterTransactionsColumnDefs = this.actionsColumnDef.concat(defaultMasterTransactionsTabColumnDefs);
            }
            if (this.hasNoTransactions) {
              this.store.dispatch(MasterTransactionsTableActions.hasEmptyResponse());
            }
            this.store.dispatch(MasterTransactionsTableActions.currentTabUpdated({ currentTab: masterTransactionsTableTabs.transactionStatus }));
            this.getTableData();
            break;

          case 'recurring':
            this.currentSelectedTabDefaults = this.actionsColumnDef.concat(defaultMasterRecurringTabColumnDefs);
            this.fileName = `MoveMoneyMasterRecurringTxnList_${this.accountNumber}_`
            this.store.select(selectMasterRecurringTab).pipe(takeUntil(this.destroy$)).subscribe(tab => {
              this.columnState = tab?.columns;
              this.columnTab = tab?.tabName ?? '';
            });

            if (this.columnState && this.columnTab) {
              this.masterTransactionsColumnDefs = this.buildColumnPreferences(this.columnState, this.columnTab);
            }
            else {
              this.masterTransactionsColumnDefs = this.actionsColumnDef.concat(defaultMasterRecurringTabColumnDefs);
            }
            if (this.hasNoRecurringTransactions) {
              this.store.dispatch(MasterTransactionsTableActions.hasEmptyResponse());
            }
            this.store.dispatch(MasterTransactionsTableActions.currentTabUpdated({ currentTab: masterTransactionsTableTabs.recurring }));
            this.getTableData();
            break;

          case 'activityReport':
            this.currentSelectedTabDefaults =  this.activityReportDownload.concat(defaultMasterActivityReportTabColumnDefs);
            this.store.select(selectMasterActivityReportTab).pipe(takeUntil(this.destroy$)).subscribe(tab => {
              this.columnState = tab?.columns;
              this.columnTab = tab?.tabName ?? '';
            });
            this.currentTabSelector = selectActivityReports;

            if (this.columnState && this.columnTab) {
              this.masterTransactionsColumnDefs = this.buildColumnPreferences(this.columnState, this.columnTab);
            }
            else {
              this.masterTransactionsColumnDefs = this.activityReportDownload.concat(defaultMasterActivityReportTabColumnDefs);
            }
            if (this.hasNoActivityReport) {
              this.store.dispatch(ActivityReportTableActions .hasEmptyActivityReportResponse());
            }
            this.store.dispatch(MasterTransactionsTableActions.currentTabUpdated({ currentTab: masterTransactionsTableTabs.activityReport }));
            this.isActivityReportTab = true;
            this.getTableData();
            break;

          default:
            this.currentSelectedTabDefaults = defaultMasterTransactionsTabColumnDefs;
            this.masterTransactionsColumnDefs = defaultMasterTransactionsTabColumnDefs;
            this.store.dispatch(MasterTransactionsTableActions.currentTabUpdated({ currentTab: masterTransactionsTableTabs.transactionStatus }));
            this.getTableData();
          }
      }
      ));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  actionLinkClicked(event: any) {
    // TO-DO for future actions
    if (event.name === 'View') {
      this.statusClicked(event);
    }
  }

  statusClicked(event: any) {
    this.store.dispatch(MasterTransactionsTableActions.loadViewPage({ viewType: 'master-transaction' }));
    this.callTransactionInquiry(event, true);
  }

  downloadClicked(activityReportId: string, interimSequenceId: string) {
    this.store.dispatch(
      ActivityReportTableActions .downloadActivityReportClicked({
        activityReportId,
        interimSequenceId
      })
    );
  }

  callTransactionInquiry(event: any, isFromView: boolean) {
      this.store.pipe(takeUntil(this.destroy$),
        select(this.currentTabSelector),
        map((element: any) => element.find((transaction: any) => { return transaction.attributes.cashieringRqstId === event.rowData['CashieringRqstId'] })),
        filter(res => res !== undefined)
      ).subscribe((res) => {
        if (res.attributes.typeDescription?.includes('Journal') || res.attributes.typeDescription?.includes('Internal Transfer')) {
          this.store.dispatch(MasterStatusViewActions.callMsvInternalTransferInquiry({
            payload: res,
            viewType: this.isSloa(event.rowData.Type) ? 'standing-auth' : 'transaction',
            transactionType: transactionTypes.internalTransfer
          }));
        } else if (res.attributes.typeDescription?.includes('MoneyLink') || res.attributes.typeDescription?.includes('ACH')) {
         this.store.dispatch(MasterStatusViewActions.callMsvAchInquiry({
           payload: res,
           viewType: this.isSloa(event.rowData.Type) ? 'standing-auth' : 'transaction',
           transactionType: transactionTypes.ach
         }));
        } else if (res.attributes.typeDescription?.includes('Wire')) {
          const isStandingAuth = this.isSloa(event.rowData.Type);
          const viewType = isStandingAuth ? 'standing-auth' : 'transaction';
          const wireTransactionType = isStandingAuth ? transactionTypes.wireSloa : transactionTypes.wire;
          const resourceVersion ='3';

          this.store.dispatch(MasterStatusViewActions.callMsvWireInquiry({
            payload: res,
            viewType: viewType,
            transactionType: wireTransactionType,
            resourceVersion: resourceVersion
          }));
        } else if (res.attributes.typeDescription?.includes('Check')) {
          this.store.dispatch(MasterStatusViewActions.callMsvCheckInquiry({
            payload: res,
            viewType: this.isSloa(event.rowData.Type) ? 'standing-auth' : 'transaction',
            transactionType: transactionTypes.check
          }));
        }
      })
    }

  updateGridReady(ready: boolean) {
    this.store.dispatch(gridReady({ gridReady: ready, tab: this.currentTab === 'Recurring' ? masterTransactionsTableTabs.recurring : this.currentTab === 'Activity Report' ? masterTransactionsTableTabs.activityReport : masterTransactionsTableTabs.transactionStatus }));
  }

  mapTransactionListDataToRowData(transactions: MsvTransaction[]): MasterTransactionsTableColumn[] {
      return transactions.map((transaction) => {
          return this.mapTransaction(transaction.attributes);
      });
  }

  mapMasterActivityReportsDataToRowData(activityReports: ActivityReport[]): MasterActivityReportTableColumn[] {
    return activityReports.map((activityReport) => {
      return {
        "ActivityReportDownload": activityReport.id,
        "InterimSeqId": activityReport.interimSeqId,
        "Date": activityReport.description
      }
    });
  }


  mapTransaction(transaction: MsvTransactionAttributes): MasterTransactionsTableColumn{
      return {
          "Account number": transaction.formattedAccountNumber,
          "Account name": transaction.accountName ?? '',
          "Registration type": transaction.registrationType ?? '',
          "Process date": transaction.formattedProcessDate.replaceAll('-', '/'),
          "Type": this.mapMediaTypeCode(transaction.mediaTypeCd) + ' - ' + this.mapPartyType(transaction),
          "To/From": this.namePipe.transform(
            `${transaction.toForStatusAndRecurring}`
          ),
          "Direction": transaction.outIn,
          Amount: this.isSloa(transaction.mediaTypeCd) ? 'N/A' : transaction.outgoingAmount !== '' ? transaction.outgoingAmount : transaction.incomingAmount,
          "Available cash": transaction.availableCash,
          "Status": transaction.statusCodeDescription,
          "CashieringRqstId": transaction.cashieringRqstId,
          "CashieringRqstSecondId": transaction.cashieringRqstSecondId,
          "Frequency":  this.mapFrequency(transaction.frequencyCd),
          "Next process date": transaction.formattedProcessDate.replaceAll('-', '/')
      }
  }

  mapMediaTypeCode(mediaTypeCd: string): string {
    switch (mediaTypeCd) {
      case 'Internal Transfer Cash':
        return "Internal transfer - Cash"
      case 'Internal Transfer-Positions':
        return "Internal transfer - Positions"
      case 'Internal Transfer-Cash/Positions':
        return "Internal transfer - Cash/Positions"
      case 'Recurring Internal Transfer Set-Up':
        return "Recurring Internal transfer"
      case 'Internal Transfer':
        return "Internal transfer"
      default: return mediaTypeCd
    }
  }
  mapFrequency(frequencyCd: string): string {
    switch (frequencyCd) {
      case '2 Times/Month':
        return "2 times/month"
      case 'Semi-Annual':
        return "Semi-annual"
      case 'Last Biz Day':
        return "Last biz day"
      case 'Weekly-Mon':
        return "Weekly - Mon"
      case 'Weekly-Tue':
        return "Weekly - Tue"
      case 'Weekly-Wed':
        return "Weekly - Wed"
      case 'Weekly-Thurs':
        return "Weekly - Thurs"
      case 'Weekly-Fri':
        return "Weekly - Fri"
      default:
        return frequencyCd
    }
  }

  mapPartyType(transaction: MsvTransactionAttributes): string {
    if (transaction.thirdPartyInd === 'I') {
      return 'No IA Authority';
    }
    if (transaction.isThirdPartyInd) {
      return '3rd Party';
    } else {
      return '1st Party';
    }
  }

  isSloa(transactionType: string): boolean {
    return !!(transactionType && (transactionType.includes('SLOA') || transactionType.includes('On-Request')));
  }
}
